/* The wallet: my wallet, favourites and discover, as the artboard draws it.
 *
 * The switch is the artboard's. What sits under each tab is what is true today:
 * the cards, the ones hearted on this device, and a Discover with nothing listed
 * in its own empty state (ruled 2026-09-17: empty ships, wrong does not).
 *
 * ORDER lives in `@damga/kit/ranking`: a stamp card and a points card are
 * compared by the fraction completed, claimable first. Archived cards come back
 * from the API on purpose and go to their own screen.
 */
import { plural, rankCards, t } from "@damga/kit";

import type { Card } from "../App";
import { hueFor, onDate } from "../ui";
import { Discover } from "./Discover";

export type WalletView = "mine" | "favs" | "discover";

export function Wallet({
  view,
  onView,
  cards,
  asked,
  favourites,
  onToggleFavourite,
  onOpenCard,
  onOpenArchive,
  onShowCode,
  onRefresh,
  refreshing,
}: {
  view: WalletView;
  onView: (v: WalletView) => void;
  cards: Card[] | null;
  asked: boolean;
  favourites: string[];
  onToggleFavourite: (card: Card) => void;
  onOpenCard: (id: string) => void;
  onOpenArchive: () => void;
  onShowCode: () => void;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const live = rankCards((cards ?? []).filter((c) => !c.archived_at));
  const archived = (cards ?? []).filter((c) => c.archived_at);
  const favs = live.filter((c) => favourites.includes(c.membership_id));

  const switcher = (
    <div className="seg" role="tablist" aria-label={t("wallet.title")}>
      {(
        [
          ["mine", t("wallet.switch.mine")],
          ["favs", favourites.length ? `${t("wallet.switch.favs")} ${favs.length}` : t("wallet.switch.favs")],
          ["discover", t("wallet.switch.discover")],
        ] as const
      ).map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={view === key}
          onClick={() => onView(key as WalletView)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (view === "discover") {
    return (
      <>
        {switcher}
        <Discover onShowCode={onShowCode} />
      </>
    );
  }

  if (view === "favs") {
    return (
      <>
        {switcher}
        {favs.length === 0 ? (
          <div className="empty">
            <p className="empty-title">{t("favs.empty.title")}</p>
            <p className="empty-body">{t("favs.empty.body")}</p>
          </div>
        ) : (
          <ul className="cards">
            {favs.map((card) => (
              <li key={card.membership_id}>
                <CardRow
                  card={card}
                  favourite
                  onToggleFavourite={() => onToggleFavourite(card)}
                  onOpen={() => onOpenCard(card.membership_id)}
                />
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }

  if (!asked && cards === null) return <p className="muted">{t("common.loading")}</p>;

  if (live.length === 0) {
    return (
      <>
        {switcher}
        <div className="empty">
          <p className="empty-title">{t("wallet.empty.title")}</p>
          <p className="empty-body">{t("wallet.empty.body")}</p>
          <button type="button" className="btn btn-primary" onClick={onShowCode}>
            {t("wallet.showCode")}
          </button>
        </div>
        <ArchiveLink count={archived.length} onOpen={onOpenArchive} />
      </>
    );
  }

  return (
    <>
      {switcher}
      <div className="section-head">
        <p className="label">{plural("wallet.count", live.length)}</p>
        {/* The wallet refreshes itself for half a minute after the code is
            shown, and on waking. This is for the rest of the time. */}
        <button type="button" className="link-mono" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? t("common.loading") : t("wallet.refresh")}
        </button>
      </div>
      <ul className="cards">
        {live.map((card) => (
          <li key={card.membership_id}>
            <CardRow
              card={card}
              favourite={favourites.includes(card.membership_id)}
              onToggleFavourite={() => onToggleFavourite(card)}
              onOpen={() => onOpenCard(card.membership_id)}
            />
          </li>
        ))}
      </ul>
      <ArchiveLink count={archived.length} onOpen={onOpenArchive} />
    </>
  );
}

function ArchiveLink({ count, onOpen }: { count: number; onOpen: () => void }) {
  if (count === 0) return null;
  return (
    <button type="button" className="btn btn-quiet" onClick={onOpen}>
      {plural("archive.link", count)}
    </button>
  );
}

/* WHEN, SAID THE WAY A PERSON SAYS IT. */
export function lastSeen(iso: string): string {
  const at = new Date(iso);
  const now = new Date();
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(now) - midnight(at)) / 86_400_000);
  if (days <= 0) return t("time.today");
  if (days === 1) return t("time.yesterday");
  if (days < 14) return t("time.daysAgo", "en", { n: days });
  return onDate(iso);
}

/* The progress a card is on, in the words the card uses everywhere.
 *
 * THE TRUE BALANCE, NEVER CLAMPED. The artboard's comment on `rung()` is a
 * product rule: a balance past the threshold (an import can do that) is shown
 * as it is, and only the pips are capped at the card's length. */
export function progressLine(card: Card): string {
  if (card.claimable > 0) return t("wallet.readyToClaim");
  return t("wallet.progress", "en", { balance: card.balance, threshold: card.threshold });
}

/* Pips while a card is short enough for them to be counted at a glance; a bar
 * after that, because forty pips is a texture rather than a count. */
const MAX_PIPS = 20;

/* THE CARD IS NOT A BUTTON ANY MORE, because the heart is a button and a button
 * cannot be inside one. The card is a group; the name is the control that opens
 * it, stretched over the whole card by its ::after; the heart sits above that. */
export function CardRow({
  card,
  favourite,
  onToggleFavourite,
  onOpen,
}: {
  card: Card;
  favourite: boolean;
  onToggleFavourite: () => void;
  onOpen: () => void;
}) {
  const points = card.program_type === "points";
  const closing = card.status === "closing";
  const filled = Math.min(card.balance, card.threshold);
  const usePips = !points && card.threshold <= MAX_PIPS;

  return (
    <div
      className="card"
      data-closing={closing ? "" : undefined}
      style={{ ["--hue" as string]: hueFor(card.business_id) }}
    >
      <div className="card-top">
        <div className="shrink">
          <button type="button" className="card-open" onClick={onOpen}>
            {card.business_name}
          </button>
          <p className="card-sub">{card.reward_description}</p>
        </div>
        <div className="card-marks">
          {/* One chip, and the claimable one wins: a card can be both closing
              and claimable, and then the reward is the urgent fact. */}
          {card.claimable > 0 ? (
            <span className="chip" data-ready="">
              {t("wallet.readyToClaim")}
            </span>
          ) : closing ? (
            <span className="chip">{t("card.closing.label")}</span>
          ) : null}
          <button
            type="button"
            className="heart"
            data-on={favourite ? "" : undefined}
            aria-pressed={favourite}
            aria-label={t(favourite ? "favs.remove" : "favs.add", "en", { name: card.business_name })}
            onClick={onToggleFavourite}
          >
            {favourite ? "♥" : "♡"}
          </button>
        </div>
      </div>

      {usePips ? (
        <span className="pips" aria-hidden="true">
          {Array.from({ length: card.threshold }, (_, i) => (
            <span key={i} className="pip" data-on={i < filled ? "" : undefined} />
          ))}
        </span>
      ) : (
        <span className="bar-track" aria-hidden="true">
          <span
            className="bar-fill"
            style={{ ["--pct" as string]: String(card.threshold ? filled / card.threshold : 0) }}
          />
        </span>
      )}

      {/* Only when something is claimable. A date on a card with no reward is
          a deadline for nothing, and reads as a threat to the stamps. */}
      {card.claimable > 0 && card.reward_expires_at && (
        <p className="card-sub">{t("wallet.useBy", "en", { date: onDate(card.reward_expires_at) })}</p>
      )}

      {closing && (
        <div className="closing" role="note">
          <p>{t("card.closing.stopped")}</p>
          {card.claimable > 0 && card.claimable_until ? (
            <p>{t("card.closing.claimBy", "en", { date: onDate(card.claimable_until) })}</p>
          ) : card.claimable_until ? (
            <p>{t("card.closing.until", "en", { date: onDate(card.claimable_until) })}</p>
          ) : null}
        </div>
      )}

      <span className="card-foot">
        <span>{progressLine(card)}</span>
        {card.last_earn_at && <span>{lastSeen(card.last_earn_at)}</span>}
      </span>
    </div>
  );
}
